import type { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  accountStatusLabel,
  accountStatusTone,
  batchStatusLabel,
  batchStatusTone,
  messageStateLabel,
  messageStateTone,
  toneBadgeClass,
  toneSolidClass,
  type Tone,
} from "@/lib/format";
import type { BatchStatus, MessageState } from "@/gen/bmi/v1/batch_message_pb";
import type { AccountStatus } from "@/gen/wabms/admin/v1/admin_pb";

interface ToneBadgeProps {
  tone: Tone;
  children: ReactNode;
  className?: string;
}

/** The one badge everything status-shaped renders through. */
export function ToneBadge({ tone, children, className }: ToneBadgeProps) {
  return (
    <Badge variant="outline" className={cn(toneBadgeClass(tone), "border", className)}>
      {children}
    </Badge>
  );
}

export function StatusDot({
  tone,
  pulse = false,
  className,
}: {
  tone: Tone;
  pulse?: boolean;
  className?: string;
}) {
  return (
    <span className={cn("relative inline-flex size-2.5 shrink-0", className)}>
      {pulse ? (
        <span
          className={cn(
            "absolute inline-flex size-full animate-ping rounded-full opacity-60",
            toneSolidClass(tone),
          )}
        />
      ) : null}
      <span className={cn("relative inline-flex size-2.5 rounded-full", toneSolidClass(tone))} />
    </span>
  );
}

export function MessageStateBadge({
  state,
  className,
}: {
  state: MessageState;
  className?: string;
}) {
  return (
    <ToneBadge tone={messageStateTone(state)} className={className}>
      {messageStateLabel(state)}
    </ToneBadge>
  );
}

export function BatchStatusBadge({ status, className }: { status: BatchStatus; className?: string }) {
  return (
    <ToneBadge tone={batchStatusTone(status)} className={className}>
      {batchStatusLabel(status)}
    </ToneBadge>
  );
}

export function AccountStatusBadge({
  status,
  className,
}: {
  status: AccountStatus;
  className?: string;
}) {
  return (
    <ToneBadge tone={accountStatusTone(status)} className={className}>
      {accountStatusLabel(status)}
    </ToneBadge>
  );
}
